import { Link } from '@tanstack/react-router';
import { agencyDetailsLink } from '../../../campaigns/hierarchyLinks';
import { useState } from 'react';
import { Plus, Pencil } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardTableRowActionItem,
  DashboardTableRowActions,
  DashboardTableSearchInput,
  buildDashboardDataTableColumns,
  DashboardDataTableColumnKind,
  dashboardTableTextActionClassName,
} from '@shigo-ui/dashboard-ui';
import { Button } from '@shigo-ui/ui';
import { dashboardT } from '../../../../lib/messages';
import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';
import { ShigoPermission } from '../../../auth/permissions';
import { useShigoPermissions } from '../../../auth/useShigoPermissions';
import { agencyPageSizes, type Agency } from '../model';
import { useAgenciesQuery } from '../hooks';
import { useAgencyRouteFilters } from '../useAgencyRouteFilters';
import { AgencyDialog } from '../components/AgencyDialog/AgencyDialog';

export function AgencyPage() {
  const { can } = useShigoPermissions();
  const filters = useAgencyRouteFilters();
  const query = useAgenciesQuery({ name: filters.search, page: filters.page, size: filters.size });
  const search = useDebouncedValue(filters.search, filters.setSearch, 650);
  const [editor, setEditor] = useState<{ agency: Agency | null } | null>(null);
  const canWrite = can(ShigoPermission.SupplyWrite);
  const columns = buildDashboardDataTableColumns<Agency>([
    {
      id: 'name',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('agency.name'),
      value: (agency) => (
        <Link {...agencyDetailsLink(agency.id)} className={dashboardTableTextActionClassName()}>
          {agency.name}
        </Link>
      ),
    },
    {
      id: 'actions',
      kind: DashboardDataTableColumnKind.Actions,
      hidden: !canWrite,
      cell: (agency) => (
        <DashboardTableRowActions label={dashboardT('supply.actions')}>
          <DashboardTableRowActionItem
            onClick={() => {
              setEditor({ agency });
            }}
          >
            <Pencil aria-hidden="true" />
            {dashboardT('nav.action.edit')}
          </DashboardTableRowActionItem>
        </DashboardTableRowActions>
      ),
    },
  ]);

  return (
    <main className="page-gutter min-w-0 flex-1 py-4 md:py-6">
      <div className="mx-auto flex w-full max-w-[1920px] min-w-0 flex-col gap-6">
        <DashboardDataTable
          title={dashboardT('agency.title')}
          tableLabel={dashboardT('agency.tableLabel')}
          columns={columns}
          items={query.data?.content ?? []}
          loading={query.isLoading}
          errorMessage={query.isError ? dashboardT('agency.loadError') : undefined}
          pagination={{
            value: {
              page: filters.page,
              pageSize: filters.size,
              totalItems: query.data?.totalElements ?? 0,
            },
            pageSizes: agencyPageSizes,
            disabled: query.isFetching,
            onChange: (next) => {
              filters.setPagination(next.page, next.pageSize);
            },
          }}
          getRowKey={(agency) => agency.id}
          emptyMessage={dashboardT('agency.empty')}
          minWidthClassName="min-w-0"
          toolbar={
            <>
              <DashboardTableSearchInput
                aria-label={dashboardT('agency.search')}
                placeholder={dashboardT('agency.search')}
                value={search.draft}
                onChange={(event) => {
                  search.setDraft(event.target.value);
                }}
              />
              {canWrite ? (
                <Button
                  onClick={() => {
                    setEditor({ agency: null });
                  }}
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  {dashboardT('agency.add')}
                </Button>
              ) : null}
            </>
          }
        />
      </div>
      {editor && canWrite ? (
        <AgencyDialog
          agency={editor.agency}
          onClose={() => {
            setEditor(null);
          }}
        />
      ) : null}
    </main>
  );
}
